/**
 * CapabilityTourStep — six capability cards, each with a one-line value
 * promise and a deep-link to the live feature. Two columns on desktop,
 * one on mobile. Cards animate in stagger so the grid feels assembled
 * rather than dropped.
 *
 * The "See it live" links route into the actual app — closing the loop
 * between "this is what the product does" and "go try it now". Routes
 * point at synthetic / public surfaces so they work even before the
 * inspector has assignments.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Map,
  ClipboardCheck,
  Sparkles,
  Mic,
  BookOpen,
  History,
  ArrowUpRight,
  LayoutDashboard,
  ShieldCheck,
} from 'lucide-react';
import StepShell from './StepShell';
import StatusPill from '../../../components/brand/StatusPill';
import { StepId } from '../types';

interface Props {
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  /** Called whenever the inspector follows a "see it live" link OR the
   *  big demo CTA. Marks onboarding as complete so the ProtectedRoute
   *  gate doesn't redirect them straight back to /onboarding. */
  onLeaveTour: () => void;
}

interface Capability {
  Icon: React.ComponentType<{ className?: string }>;
  tint: 'cyan' | 'emerald' | 'amber' | 'blue';
  title: string;
  promise: string;
  body: string;
  href?: string;
  hrefLabel?: string;
  exampleEl: React.ReactNode;
}

const CAPABILITIES: Capability[] = [
  {
    Icon: Map,
    tint: 'cyan',
    title: 'See any vessel, anywhere',
    promise: 'Live AIS map with risk overlay.',
    body: 'Cluster, filter, and inspect any of 28K+ live vessels. Tap a marker for owner, manager, last 30 days of port calls, and active sanctions exposure.',
    href: '/',
    hrefLabel: 'Open the live map',
    exampleEl: (
      <div className="rounded-md bg-slate-950/80 border border-slate-800 px-3 py-2 text-[11px] font-mono text-cyan-300">
        <span className="text-slate-500">tracking</span>
        <span className="ml-2 text-cyan-300 font-bold">28,432</span>
        <span className="text-slate-500"> vessels · </span>
        <span className="text-emerald-300">live</span>
      </div>
    ),
  },
  {
    Icon: ClipboardCheck,
    tint: 'cyan',
    title: 'Run a regulator-grade inspection',
    promise: 'CDI / RISQ / TMSA workbench, offline-capable.',
    body: 'CDI sections O–14 plus RISQ 3.2 — 1,900+ questions with booklet guidance, conditional sub-questions, and severity categories (S/R/D/NS · M/V), resumable on the bridge or in port.',
    href: '/vetting',
    hrefLabel: 'Browse inspection workbench',
    exampleEl: (
      <div className="flex flex-wrap gap-1.5">
        <StatusPill variant="non_compliant" size="sm" label="Section 3.2" />
        <StatusPill variant="observation"   size="sm" label="Section 4.7" />
        <StatusPill variant="compliant"     size="sm" label="Section 5.1" />
      </div>
    ),
  },
  {
    Icon: Sparkles,
    tint: 'amber',
    title: 'AI-drafted reports in seconds',
    promise: 'Inspector authors. AI handles the prose.',
    body: 'A regulator-format markdown draft from your answers and observations — vessel particulars table, findings by section, certification footer. Edit any line; the AI never speaks for you.',
    href: '/vetting',
    hrefLabel: 'See a sample draft',
    exampleEl: (
      <div className="rounded-md bg-slate-950/80 border border-slate-800 px-3 py-2 text-[11px] font-mono text-amber-300">
        <span className="text-slate-500"># Findings By Section</span>
        <span className="ml-2 text-white">| Section | S | R | D |</span>
      </div>
    ),
  },
  {
    Icon: Mic,
    tint: 'cyan',
    title: 'Voice-driven observations',
    promise: 'Speak findings, hands-free.',
    body: 'Web Speech API streams your dictation into the observation modal in real time, auto-restarts on pause, and never sends audio off-device.',
    exampleEl: (
      <div className="rounded-md bg-slate-950/80 border border-slate-800 px-3 py-2 text-[11px] text-slate-300 italic">
        <span className="text-cyan-300">●</span> "Lifeboat davit number two shows
        signs of corrosion at the…"
      </div>
    ),
  },
  {
    Icon: BookOpen,
    tint: 'blue',
    title: 'Standards always at hand',
    promise: 'Every observation, every IMO citation.',
    body: 'Each question is pre-mapped to SOLAS / MARPOL / ISM / ISPS / MLC clauses. Citations carry into the report automatically — no copy-paste, no missed references.',
    exampleEl: (
      <div className="flex flex-wrap gap-1.5">
        <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-blue-500/40 bg-blue-500/10 text-blue-300">SOLAS III/20</span>
        <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-blue-500/40 bg-blue-500/10 text-blue-300">MARPOL I/14</span>
        <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-blue-500/40 bg-blue-500/10 text-blue-300">ISM 10.2</span>
      </div>
    ),
  },
  {
    Icon: History,
    tint: 'emerald',
    title: 'Build a fleet vetting history',
    promise: 'Every inspection, every vessel, forever.',
    body: 'Per-vessel timeline of prior inspections, recurring observations, signed reports. Spot a deficiency that\'s reappearing across calls before the next regulator does.',
    exampleEl: (
      <div className="rounded-md bg-slate-950/80 border border-slate-800 px-3 py-2 text-[11px] font-mono text-emerald-300">
        <span className="text-slate-500">last 90d ·</span>
        <span className="ml-2 text-emerald-300 font-bold">12 inspections</span>
        <span className="text-slate-500"> · </span>
        <span className="text-amber-300">3 recurring</span>
      </div>
    ),
  },
  {
    Icon: LayoutDashboard,
    tint: 'cyan',
    title: 'Your personal workspace',
    promise: 'Open work, recent activity, insights — one screen.',
    body: 'A single landing at /workspace each morning: in-progress inspections, last 7-day activity, your most-common observation category, average inspection time, and pinned bookmarks.',
    href: '/workspace',
    hrefLabel: 'Open my workspace',
    exampleEl: (
      <div className="grid grid-cols-3 gap-1.5 text-[11px] font-mono">
        <span className="px-2 py-1 rounded bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-center">3 open</span>
        <span className="px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-center">12 done</span>
        <span className="px-2 py-1 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300 text-center">9 obs</span>
      </div>
    ),
  },
  {
    Icon: ShieldCheck,
    tint: 'emerald',
    title: 'Vessel readiness score',
    promise: 'A 0–100 triage signal on every vessel.',
    body: 'Computed from class status, age, sanctions, and the last 24 months of PSC history. Click into the breakdown to audit every penalty — never a black box.',
    href: '/vessels',
    hrefLabel: 'Browse vessels',
    exampleEl: (
      <div className="flex items-center gap-2 text-[11px]">
        <span className="px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-200 font-bold">87 · READY</span>
        <span className="px-2 py-1 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-200 font-bold">62 · WATCH</span>
      </div>
    ),
  },
];

const TINT_CLS: Record<Capability['tint'], { ring: string; chip: string; icon: string }> = {
  cyan:    { ring: 'hover:border-cyan-500/40',    chip: 'bg-cyan-500/10 border-cyan-500/30',       icon: 'text-cyan-300' },
  emerald: { ring: 'hover:border-emerald-500/40', chip: 'bg-emerald-500/10 border-emerald-500/30', icon: 'text-emerald-300' },
  amber:   { ring: 'hover:border-amber-500/40',   chip: 'bg-amber-500/10 border-amber-500/30',     icon: 'text-amber-300' },
  blue:    { ring: 'hover:border-blue-500/40',    chip: 'bg-blue-500/10 border-blue-500/30',       icon: 'text-blue-300' },
};

export default function CapabilityTourStep({ onNext, onBack, onSkip, onLeaveTour }: Props) {
  return (
    <StepShell
      step={'capabilities' as StepId}
      onBack={onBack}
      onNext={onNext}
      onSkip={onSkip}
      title="What you can do tomorrow morning"
      subtitle="Six capabilities, one workbench. Each one has been built around what inspectors told us slowed them down most."
    >
      {/* Sales-grade demo CTA — pinned above the capability grid so it's
          the first thing inspectors see on this step. Lets a prospect
          experience the product end-to-end without signing in. */}
      <Link
        to="/vetting/demo"
        onClick={onLeaveTour}
        className="block mb-5 rounded-xl border border-cyan-500/40 bg-gradient-to-r from-cyan-500/10 to-blue-600/10 hover:from-cyan-500/15 hover:to-blue-600/15 p-4 transition-all"
        style={{ boxShadow: '0 0 30px rgba(6,182,212,0.10)' }}
      >
        <div className="flex items-center gap-4">
          <div className="shrink-0 w-11 h-11 rounded-lg bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center">
            <span className="text-2xl" aria-hidden="true">🎬</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold">
              5-minute interactive demo
            </div>
            <div className="text-sm text-white font-semibold mt-0.5">
              See the whole inspection journey on a sample vessel
            </div>
            <div className="text-[11px] text-slate-400 mt-1">
              Pre-inspection brief → CDI questionnaire → AI report → signing.
              Realistic data, no login required.
            </div>
          </div>
          <ArrowUpRight className="w-5 h-5 text-cyan-300 shrink-0" />
        </div>
      </Link>

      <div className="grid md:grid-cols-2 gap-4">
        {CAPABILITIES.map((c, i) => (
          <div
            key={c.title}
            className={`rounded-xl bg-slate-900/60 border border-slate-800 p-5 transition-all duration-200 ${TINT_CLS[c.tint].ring}`}
            style={{
              animation: `fadeInUp 0.5s ${i * 80}ms backwards`,
            }}
          >
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg border flex items-center justify-center shrink-0 ${TINT_CLS[c.tint].chip}`}>
                <c.Icon className={`w-5 h-5 ${TINT_CLS[c.tint].icon}`} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-white leading-tight">{c.title}</h3>
                <div className={`text-xs font-semibold mt-0.5 ${TINT_CLS[c.tint].icon}`}>
                  {c.promise}
                </div>
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">{c.body}</p>
            <div className="mt-3.5">{c.exampleEl}</div>
            {c.href && (
              <Link
                to={c.href}
                onClick={onLeaveTour}
                className={`mt-3 inline-flex items-center gap-1 text-xs font-semibold ${TINT_CLS[c.tint].icon} hover:underline`}
              >
                <span>{c.hrefLabel}</span>
                <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        ))}
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </StepShell>
  );
}
